/* eslint-disable react/jsx-props-no-spreading */
import PropTypes from 'prop-types';
import { Controller, useForm } from 'react-hook-form';
import { joiResolver } from '@hookform/resolvers/joi';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
// utils
import { format } from 'date-fns';
import { cn } from '@repo/zolintra-utils';
// components
import {
  Button,
  TextField,
  MessageBanner,
  DropdownList,
  InputsRadioGroup,
} from '@repo/zolintra-ui';
// validations
import { useMemo } from 'react';
import { postLeavePostSchemaAny, postLeavePostSchemaOwn } from '../../../validations/postLeavePostSchema';
// hooks
import usePost from '../../../hooks/domain/usePost';
import { useDialog } from '../../../hooks/useDialog';
import useScrollTo from '../../../hooks/useScrollTo';
import useToast from '../../../hooks/useToast';
import useResources from '../../../hooks/domain/useResources';
import useLeavePost, { LEAVE_POST_TYPES_ANY } from '../../../hooks/domain/useLeavePost';
import useSearchParams from '../../../hooks/useSearchParams';
import useFormUtils from '../../../hooks/useFormUtils';
import { useAppContext } from '../../../store/AppContext';
// permissions
import hasPermission, { PERMISSIONS } from '../../../permissions/hasPermission';
// services
import { useResourcesListQuery, useResourcesStaffListQuery } from '../../../services/resource/resourceQueries';
import { postLeavePost } from '../../../services/leavePost/leavePostService';
import { useConfigurationQuery } from '../../../services/configuration/configurationQueries';
// constants
import { VIEW_DOMAIN } from '../../../constants';

import styles from './Form.module.css';

function NewLeaveForm({ event, isAdminView }) {
  // Hooks
  const { t } = useTranslation();
  const [{ user }] = useAppContext();
  const {
    getSlotsIdFromDate,
    handleTwoYearsStraddleForOnePeriod,
    handleAPIErrorMessage,
    timesOfDayOptions,
  } = usePost();
  const { setDialog } = useDialog();
  const { scrollToFirstError } = useScrollTo();
  const toast = useToast();
  const { transformResourcesListToOptions, getStaffResourcesListWithUserInfo } = useResources();
  const { getTypesOptions } = useLeavePost();
  const { updateSearchParams } = useSearchParams();
  const { getFieldError } = useFormUtils();

  const hasCreateAnyAndResourcePermission = (
    hasPermission(PERMISSIONS.GET_ANY_RESOURCE, user.prm)
    && hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)
    && hasPermission(PERMISSIONS.IDENTITY_ACCESS_GET_ANY_RESOURCE, user.prm)
  );

  const leaveFormDefaultValues = {
    resourceId: null,
    type: { value: 'Paid', label: t('leavePost.types.Paid') },
    startDate: null,
    startDateSlot: null,
    endDate: null,
    endDateSlot: null,
    comment: '',
  };

  // Leave form
  const {
    control,
    register,
    handleSubmit,
    formState: { errors: formErrors },
    watch,
    setValue,
    trigger,
    getValues,
  } = useForm({
    defaultValues: {
      ...leaveFormDefaultValues,
      ...event,
      startDate: event?.startDate,
      endDate: event?.endDate,
    },
    resolver: joiResolver(
      hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)
        ? postLeavePostSchemaAny
        : postLeavePostSchemaOwn,
    ),
    mode: 'onBlur',
    shouldFocusError: false,
  });
  const endDate = watch('endDate');
  const startDateSlot = watch('startDateSlot');
  const endDateSlot = watch('endDateSlot');
  const resourceId = watch('resourceId');

  // API Calls
  const queryClient = useQueryClient();
  const configurationQuery = useConfigurationQuery({
    enabled: hasPermission(PERMISSIONS.GET_CONFIGURATION, user.prm),
  });
  const resourcesListQuery = useResourcesListQuery({
    enabled: hasCreateAnyAndResourcePermission,
  });
  const resourcesStaffListQuery = useResourcesStaffListQuery({
    enabled: hasCreateAnyAndResourcePermission,
  });
  const resourcesListOptions = transformResourcesListToOptions(
    getStaffResourcesListWithUserInfo(
      resourcesStaffListQuery.data,
      resourcesListQuery.data,
    ),
  );

  const serializeValuesForApi = (values) => {
    const startDateSlots = getSlotsIdFromDate(new Date(values.startDate));
    const enDateSlots = getSlotsIdFromDate(new Date(values.endDate));

    return {
      resourceId: (
        hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)
          ? values.resourceId?.value
          : user.id
      ),
      includedStartSlotId: startDateSlots[values.startDateSlot],
      excludedEndSlotId: enDateSlots[values.endDateSlot] + 1,
      comment: values.comment,
      type: values.type.value,
      version: values?.version || 1,
    };
  };

  const { mutateAsync, isPending, error } = useMutation({
    mutationFn: (values) => postLeavePost(
      {
        // @ts-ignore - can't use typing on mutationFn
        year: new Date(values.startDate).getFullYear().toString(),
      },
      serializeValuesForApi(values),
    ),
    onSuccess: (_, values) => {
      queryClient.invalidateQueries({ queryKey: ['postsList'] });

      // @ts-ignore - can't use typing on onSuccess values
      const startDate = values?.startDate || null;

      // filter on start date
      if (startDate) {
        updateSearchParams({
          year: startDate.getFullYear(),
          month: startDate.getMonth(),
          view: VIEW_DOMAIN.LEAVE,
        }, true);
      }

      setDialog({ type: 'HIDE_DIALOG' });

      toast.notify({
        type: 'success',
        message: t('remoteWorkingAndLeave.newLeaveForm.success'),
      });
    },
  });

  // Handlers
  const handleInputDateChange = (e, onChange) => {
    onChange(e);
    trigger(e.target.name);
  };

  const handleStartDateChange = ({ e, field }) => {
    handleInputDateChange(e, field.onChange);

    // automatically set values
    if (e.target.value) {
      const start = new Date(e.target.value).getTime();
      const end = endDate ? new Date(endDate).getTime() : null;

      // set end value based on start value
      // only if end value is empty or if the end date is before chosen start date
      if (!endDate || end < start) {
        setValue('endDate', e.target.value);
        trigger('endDate');
      }

      // set slots values if empty
      if (!startDateSlot) setValue('startDateSlot', timesOfDayOptions[0].value);
      if (!endDateSlot) setValue('endDateSlot', timesOfDayOptions[1].value);
    }
  };

  // Le week-end n'est autorisé que pour certains types : les dates déjà saisies
  // doivent être revalidées quand le type change.
  const handleTypeChange = ({ option, field }) => {
    field.onChange(option);

    if (getValues('startDate')) trigger('startDate');
    if (getValues('endDate')) trigger('endDate');
  };

  const handlePostSubmit = async (data) => {
    const postsValues = handleTwoYearsStraddleForOnePeriod(data);
    const promises = postsValues.map((values) => mutateAsync(values));
    await Promise.all(promises);
  };

  const typesOptions = useMemo(() => {
    const canCreateAnyLeavePost = hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm);

    // get selected resource configuration
    const selectedResourceId = canCreateAnyLeavePost ? resourceId?.value : user.id;
    const selectedResource = resourcesStaffListQuery.data?.find((r) => r.id === selectedResourceId);
    // and get value for canPostCompensatoryTimeLeave (RTT)
    const canPostCompensatoryTimeLeave = selectedResource
      ? selectedResource.canPostCompensatoryTimeLeave
      : true;

    return (
      getTypesOptions(canCreateAnyLeavePost)
        .filter((option) => (
          !canPostCompensatoryTimeLeave
            ? option.value !== LEAVE_POST_TYPES_ANY.RECUPERATION
            : true
        ))
        .map((option) => ({ ...option, label: t(option.label) }))
    );
  }, [resourceId]);

  return (
    <div className={styles['form-container']}>
      <div className={styles['form-content']}>
        {hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm) && isAdminView ? (
          <Controller
            name="resourceId"
            control={control}
            render={({ field }) => (
              <DropdownList
                {...field}
                label={t(
                  'remoteWorkingAndLeave.newLeaveForm.resource.label',
                )}
                placeholder={t(
                  'remoteWorkingAndLeave.newLeaveForm.resource.placeholder',
                )}
                options={resourcesListOptions}
                noOptionsMessage={t('common.noOptionsMessage')}
                className="margin-bottom-m"
                error={getFieldError({ errors: formErrors, fieldName: field.name })}
                isInsideDialog
              />
            )}
          />
        ) : null}

        <Controller
          name="type"
          control={control}
          render={({ field }) => (
            <DropdownList
              {...field}
              label={t(
                'remoteWorkingAndLeave.newLeaveForm.type.label',
              )}
              placeholder={t(
                'remoteWorkingAndLeave.newLeaveForm.type.placeholder',
              )}
              options={typesOptions}
              noOptionsMessage={t('common.noOptionsMessage')}
              className="margin-bottom-m"
              error={getFieldError({ errors: formErrors, fieldName: field.name })}
              isInsideDialog
              onChange={(option) => handleTypeChange({ option, field })}
            />
          )}
        />

        <div className={cn([styles['two-columns-fields'], styles['align-bottom'], 'margin-bottom-m'])}>
          <Controller
            name="startDate"
            control={control}
            render={({ field }) => (
              <TextField
                type="date"
                label={t('remoteWorkingAndLeave.newLeaveForm.date.from')}
                error={getFieldError({ errors: formErrors, fieldName: field.name })}
                min={!hasPermission(PERMISSIONS.CREATE_POST_ANYTIME, user.prm) ? format(new Date(), 'yyyy-MM-dd') : ''}
                {...field}
                onChange={(e) => handleStartDateChange({ e, field })}
              />
            )}
          />

          <InputsRadioGroup
            {...register('startDateSlot')}
            options={timesOfDayOptions}
            error={getFieldError({ errors: formErrors, fieldName: 'startDateSlot' })}
          />
        </div>
        <div className={cn([styles['two-columns-fields'], styles['align-bottom'], 'margin-bottom-m'])}>
          <Controller
            name="endDate"
            control={control}
            render={({ field }) => (
              <TextField
                type="date"
                label={t('remoteWorkingAndLeave.newLeaveForm.date.to')}
                error={getFieldError({ errors: formErrors, fieldName: field.name })}
                {...field}
                onChange={(e) => handleInputDateChange(e, field.onChange)}
              />
            )}
          />

          <InputsRadioGroup
            {...register('endDateSlot')}
            options={timesOfDayOptions}
            error={getFieldError({ errors: formErrors, fieldName: 'endDateSlot' })}
          />
        </div>

        <TextField
          label={t(
            'remoteWorkingAndLeave.newLeaveForm.comment.label',
          )}
          placeholder={t(
            'remoteWorkingAndLeave.newLeaveForm.comment.placeholder',
          )}
          className="margin-top-m"
          error={getFieldError({ errors: formErrors, fieldName: 'comment' })}
          {...register('comment')}
        />

        {!hasPermission(PERMISSIONS.CREATE_ANY_NEW_LEAVE_POST, user.prm)
        || user?.id === resourceId?.value ? (
          <div className="margin-top-m">
            <MessageBanner color="primary" icon="infoCircle" className="w-full margin-bottom-s">
              {t('remoteWorkingAndLeave.newLeaveForm.legalNotice')}
            </MessageBanner>

            <MessageBanner color="primary" icon="happyEmoji" className="w-full">
              {t('remoteWorkingAndLeave.newLeaveForm.infoMessage')}
            </MessageBanner>
          </div>
          ) : null}

        {error ? (
          <>
            {!hasPermission(PERMISSIONS.CREATE_POST_ANYTIME, user.prm)
            && configurationQuery.data?.shortLeaveNoticePeriod
            && configurationQuery.data?.longLeaveNoticePeriod ? (
              <MessageBanner color="primary" icon="alarm" className="margin-top-m w-full">
                {t('remoteWorkingAndLeave.newLeaveForm.rulesMessage', { short: configurationQuery.data.shortLeaveNoticePeriod, long: configurationQuery.data.longLeaveNoticePeriod })}
              </MessageBanner>
              ) : null}

            <MessageBanner color="error" icon="alarm" className="margin-top-m w-full">
              {handleAPIErrorMessage(error)}
            </MessageBanner>
          </>
        ) : null}
      </div>

      <div className={styles['submit-buttons']}>
        <Button variant="bordered" onClick={() => setDialog({ type: 'HIDE_DIALOG' })}>
          {t('common.cancel')}
        </Button>

        <Button
          onClick={handleSubmit(handlePostSubmit, scrollToFirstError)}
          isLoading={isPending}
        >
          {t('remoteWorkingAndLeave.newLeaveForm.save')}
        </Button>
      </div>
    </div>
  );
}

NewLeaveForm.propTypes = {
  event: PropTypes.shape({}),
  isAdminView: PropTypes.bool,
};

NewLeaveForm.defaultProps = {
  event: null,
  isAdminView: false,
};

export default NewLeaveForm;
